/**
 * Анкета заявки: поля по скрипту обзвона → стандартный текст лида.
 *
 * Диспетчер не сочиняет описание: он заполняет то, что и так спрашивает по
 * скрипту (имя, сумма долга, тип долга, город), а текст собирается сам — в CRM
 * всегда уходит одинаково устроенное описание. Скрипт от 30.08.2026:
 * программа списания работает от 300 000 ₽, долги с залогом (ипотека,
 * автокредит) не блокируют заявку — такой лид уходит с пометкой «залог»,
 * решение принимает юрист.
 *
 * Модуль чистый и без React — правила скрипта проверяются тестами, а не руками.
 */

/** Порог программы по скрипту: «работает от трёхсот тысяч». Меньше — «Не целевой». */
export const MIN_LEAD_DEBT_RUB = 300_000;

/** Метка, которая открывает анкету: «Заявка» Фикса. */
export const LEAD_FORM_LABEL_CODE = 'lead';

/**
 * Открывать ли анкету по метке (19.10.2026). Анкета — скрипт заявки Фикса про
 * долг; другая метка-лид («Лид 2» по кухням) ставится одним нажатием, иначе
 * диспетчер упрётся в поля, которые нечем заполнить, и не поставит результат.
 */
export function opensLeadForm(label: { code: string; isLead: boolean }): boolean {
  return label.isLead && label.code === LEAD_FORM_LABEL_CODE;
}

export type DebtTypeKey = 'consumer' | 'mfo' | 'cards' | 'mortgage' | 'auto';

/** secured — залоговый долг: заявку не блокирует, но уходит с пометкой для юриста. */
export const DEBT_TYPES: readonly { key: DebtTypeKey; title: string; secured: boolean }[] = [
  { key: 'consumer', title: 'Потребкредиты', secured: false },
  { key: 'mfo', title: 'Микрозаймы', secured: false },
  { key: 'cards', title: 'Кредитные карты', secured: false },
  { key: 'mortgage', title: 'Ипотека', secured: true },
  { key: 'auto', title: 'Автокредит', secured: true },
];

/** Ответ на быстрый вопрос: null — не спрашивали, и это отдельный смысл. */
export type YesNo = 'yes' | 'no' | null;

/**
 * Быстрые вопросы скрипта (01.09.2026). Отвечаются одним кликом: печатать
 * в разговоре некогда, а покупателю лида эти четыре факта нужны всегда.
 * Необязательные: разговор идёт как идёт, и пустое поле честнее выдуманного.
 */
export const QUICK_FIELDS: readonly { key: QuickFieldKey; question: string; label: string }[] = [
  { key: 'income', question: 'Официальный доход есть?', label: 'Официальный доход' },
  { key: 'overdue', question: 'Просрочки уже есть?', label: 'Просрочки' },
  { key: 'bailiffs', question: 'Приставы или суды есть?', label: 'Приставы и суды' },
  { key: 'property', question: 'Имущество кроме единственного жилья?', label: 'Имущество' },
];

export type QuickFieldKey = 'income' | 'overdue' | 'bailiffs' | 'property';

export type LeadFormValues = {
  name: string;
  /** Как ввёл диспетчер: «450000» или «450 000». Разбирает parseDebtRub. */
  debtRub: string;
  debtTypes: readonly DebtTypeKey[];
  /**
   * «Уже работает с другой компанией по списанию?» — да/нет, null — не спрошено.
   * Покупатель лида читает это в описании (просьба Evgeniy 31.08.2026):
   * клиент в чужой работе стоит других денег, и молчание тут не «нет».
   */
  worksWithFirm: YesNo;
  /** Быстрые вопросы: доход, просрочки, приставы, имущество */
  income: YesNo;
  overdue: YesNo;
  bailiffs: YesNo;
  property: YesNo;
  city: string;
  /** «Если диспетчер услышит что-то ещё» — необязательное поле. */
  note: string;
};

/** «450 000», «450000» → 450000; всё остальное — null, а не NaN. */
export function parseDebtRub(value: string): number | null {
  const digits = value.replace(/\s/g, '');
  if (!/^\d{1,9}$/.test(digits)) return null;
  return Number(digits);
}

/**
 * Первая причина, по которой заявку отправлять нельзя; null — можно.
 * Порядок проверок повторяет порядок вопросов скрипта: диспетчер видит
 * подсказку про то поле, до которого дошёл разговор.
 */
export function leadFormIssue(values: LeadFormValues): string | null {
  if (values.name.trim() === '') return 'Спросите имя — «Как к вам обращаться?»';
  const debt = parseDebtRub(values.debtRub);
  if (debt === null) return 'Сумма долга — числом в рублях, например 450 000';
  if (debt < MIN_LEAD_DEBT_RUB) {
    return 'Долг меньше 300 000 ₽ — программа не работает, поставьте «Не целевой»';
  }
  if (values.debtTypes.length === 0) return 'Отметьте, какие долги назвал клиент';
  if (values.worksWithFirm === null) {
    return 'Спросите: уже работает с другой компанией по списанию? Отметьте «Да» или «Нет»';
  }
  if (values.city.trim() === '') return 'Спросите город — без него лид не передаётся';
  return null;
}

/**
 * Предупреждение про залог; отправку НЕ блокирует. «Только залог» по скрипту —
 * отказ, но кнопку не отбираем: договориться мог и такой клиент, решает юрист.
 */
export function securedWarning(debtTypes: readonly DebtTypeKey[]): string | null {
  const chosen = DEBT_TYPES.filter((t) => debtTypes.includes(t.key));
  const secured = chosen.filter((t) => t.secured);
  if (secured.length === 0) return null;
  if (secured.length === chosen.length) {
    return 'Только залоговые долги — по скрипту это «Не целевой». Отправить можно, решит юрист.';
  }
  return 'Есть залог — лид уйдёт с пометкой, решение примет юрист.';
}

/** «да» / «нет» / «не спросили» — третий вариант обязателен: молчание не «нет». */
export function yesNoText(value: YesNo): string {
  return value === 'yes' ? 'да' : value === 'no' ? 'нет' : 'не спросили';
}

/** 450000 → «450 000» — разряды пробелами, как пишут деньги руками. */
export function formatRub(value: number): string {
  return String(value).replace(/\B(?=(\d{3})+(?!\d))/g, ' ');
}

/**
 * Стандартный текст лида. Многострочный: так он читается и в комментарии CRM,
 * и в телеграм-уведомлении, без парсинга на той стороне.
 */
export function buildLeadComment(values: LeadFormValues): string {
  const debt = parseDebtRub(values.debtRub) ?? 0;
  const chosen = DEBT_TYPES.filter((t) => values.debtTypes.includes(t.key));
  const unsecured = chosen.filter((t) => !t.secured);
  const secured = chosen.filter((t) => t.secured);

  const lines = [
    `Имя: ${values.name.trim()}`,
    `Долг: ${formatRub(debt)} ₽ — ${chosen.map((t) => t.title.toLowerCase()).join(', ')}`,
  ];
  if (secured.length > 0) {
    lines.push(
      `Залог: ${secured.map((t) => t.title.toLowerCase()).join(', ')}` +
        (unsecured.length === 0 ? ' — беззалоговых долгов нет' : ' — лид с пометкой'),
    );
  }
  // Явные да/нет — даже когда «нет»: покупатель лида не должен гадать,
  // «не спросили» это или «спросили — нет» (просьба Evgeniy 31.08.2026)
  lines.push(
    `Ипотека: ${values.debtTypes.includes('mortgage') ? 'да' : 'нет'}` +
      ` · Автокредит: ${values.debtTypes.includes('auto') ? 'да' : 'нет'}`,
  );
  lines.push(`Работает с другой компанией: ${yesNoText(values.worksWithFirm)}`);
  lines.push(`Город: ${values.city.trim()}`);

  // Быстрые вопросы идут ВСЕГДА, включая «не спросили»: описание у каждого
  // лида устроено одинаково, а пропуск — тоже информация для покупателя
  for (const field of QUICK_FIELDS) {
    lines.push(`${field.label}: ${yesNoText(values[field.key])}`);
  }

  if (values.note.trim() !== '') lines.push(`Доп: ${values.note.trim()}`);
  return lines.join('\n');
}
